import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaPrint, FaFileExcel, FaEye } from "react-icons/fa";
import {
  getAccountStatement,
  clearAccountStatement,
  getPostingAccounts,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import { extractArray, calculatePdfPagination } from "../../../../shared/utils.js/utils";
import { convertArabicNumbersToEnglish } from "../../../../shared/utils.js/utils";

// account statement
const AccountStatement = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const {
    accountStatement,
    accountStatementTotals,
    accountStatementPagination,
    accountStatementFilters,
    accountStatementAccount,
    postingAccounts,
    isLoading,
    error,
    success,
  } = useSelector(state => state.auth);

  const [filters, setFilters] = useState({
    account_id: "",
    start_date: "",
    end_date: "",
  });
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const accountSearchInputRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [allMovements, setAllMovements] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const itemsPerPage = 10;
  const previewContainerRef = useRef(null);
  const [allMovementsForPDF, setAllMovementsForPDF] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [printPdfChunks, setPrintPdfChunks] = useState([]);
  const { t, i18n: i18nHook } = useTranslation("global");
  const isRTL = i18nHook.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;
  const postingAccountList = useMemo(
    () => extractArray(postingAccounts),
    [postingAccounts],
  );

  useEffect(() => {
    setTitle("sidenav.accountStatement");
  }, [setTitle]);

  useEffect(() => {
    dispatch(getPostingAccounts());
    return () => {
      dispatch(clearAccountStatement());
    };
  }, [dispatch]);

  useEffect(() => {
    if (accountStatementFilters) {
      setFilters({
        account_id: accountStatementFilters.account_id || "",
        start_date: accountStatementFilters.start_date || "",
        end_date: accountStatementFilters.end_date || "",
      });
    }
  }, [accountStatementFilters]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: true });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  // Update allMovements when accountStatement changes
  useEffect(() => {
    const currentMovements =
      extractArray(accountStatement) ||
      extractArray(accountStatementPagination?.data);
    if (currentMovements.length > 0) {
      setAllMovements(currentMovements);
      setLocalPage(1);
    } else {
      setAllMovements([]);
    }
  }, [accountStatement, accountStatementPagination]);

  // Get account info from accountStatementAccount or postingAccounts
  const accountInfo =
    accountStatementAccount ||
    postingAccountList.find(a => String(a.id) === String(filters.account_id)) ||
    null;

  const filteredAccounts = useMemo(() => {
    if (!accountSearchTerm) return postingAccountList;
    const term = accountSearchTerm.toLowerCase();
    return postingAccountList.filter(account => {
      const code = account.code?.toLowerCase() || "";
      const nameAr = account.name_ar?.toLowerCase() || "";
      const nameEn =
        account.name_en?.toLowerCase() || account.name?.toLowerCase() || "";
      return (
        code.includes(term) || nameAr.includes(term) || nameEn.includes(term)
      );
    });
  }, [postingAccountList, accountSearchTerm]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!filters.account_id) {
      toast.error(
        t("accountingReports.selectAccountFirst", {
          defaultValue: "برجاء اختيار الحساب أولاً",
        }),
        { rtl: isRTL },
      );
      return;
    }
    // First fetch to get total
    const result = await dispatch(
      getAccountStatement({
        account_id: filters.account_id,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: 1,
      }),
    );
    // Then fetch all with per_page = total
    if (result.payload?.data?.movements?.total) {
      const total = result.payload.data.movements.total;
      if (total > 0) {
        await dispatch(
          getAccountStatement({
            account_id: filters.account_id,
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
            per_page: total,
          }),
        );
      }
    }
    setLocalPage(1);
  };

  // Local pagination
  const totalPages = Math.ceil(allMovements.length / itemsPerPage);
  const paginatedMovements = useMemo(() => {
    const startIndex = (localPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allMovements.slice(startIndex, endIndex);
  }, [allMovements, localPage]);

  const handleLocalPageChange = page => {
    setLocalPage(page);
  };

  const totals = accountStatementTotals || {
    opening_balance: 0,
    total_debit: 0,
    total_credit: 0,
    closing_balance: 0,
  };

  const movements = paginatedMovements.length > 0 ? paginatedMovements : [];

  const formatNumber = value =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

  const formatDate = dateString => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      const locale = "en-US";
      return convertArabicNumbersToEnglish(
        date.toLocaleDateString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
      );
    } catch {
      return convertArabicNumbersToEnglish(
        dateString?.split("T")[0] || dateString || "—",
      );
    }
  };

  // Preload image helper
  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  const buildDynamicPdfChunks = (items) => {
    const topHeader = document.getElementById('account-statement-pdf-measure-top-header');
    const thead = document.getElementById('account-statement-pdf-measure-thead');
    const footer = document.getElementById('account-statement-pdf-measure-footer');
    const tbody = document.getElementById('account-statement-pdf-measure-tbody');
    return calculatePdfPagination(items, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
  };

  // Handle preview account statement
  const handlePreviewStatement = () => {
    if (!filters.account_id || !allMovements || allMovements.length === 0) {
      toast.error(
        t("accountingReports.accountStatement.preview.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const chunks = buildDynamicPdfChunks(allMovements);
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle mouse down for dragging
  const handleMouseDown = e => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - previewPosition.x,
      y: e.clientY - previewPosition.y,
    });
  };

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Handle reset to fit
  const handleResetPreview = () => {
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle print from preview
  const handlePrintFromPreview = async () => {
    await handlePrintStatement();
  };

  // Handle print account statement
  const handlePrintStatement = async () => {
    if (!filters.account_id || !allMovements || allMovements.length === 0) {
      toast.error(
        t("accountingReports.accountStatement.print.noData", {
          defaultValue: "لا توجد بيانات للطباعة",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setIsGeneratingPdf(true);
      const chunks = buildDynamicPdfChunks(allMovements);
      setPrintPdfChunks(chunks);
      setAllMovementsForPDF(allMovements);

      // Wait for DOM to update
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);

      await new Promise(resolve => setTimeout(resolve, 800));

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const selectedAccount = accountInfo;
      const accountName = selectedAccount
        ? isRTL
          ? selectedAccount.name_ar || selectedAccount.name_en || selectedAccount.name || ''
          : selectedAccount.name_en || selectedAccount.name_ar || selectedAccount.name || ''
        : '';
      const pageElementIds = chunks.map((_, i) => `account-statement-pdf-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_AccountStatement_${accountName || filters.account_id}_${new Date().toISOString().split("T")[0]}.pdf`,
        isRTL,
      });
      toast.success(
        t("accountingReports.accountStatement.print.success", {
          defaultValue: "تم إنشاء PDF بنجاح",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } catch (err) {
      console.error("Account Statement PDF error:", err);
      toast.error(
        err.message ||
          t("accountingReports.accountStatement.print.error", {
            defaultValue: "حدث خطأ أثناء إنشاء PDF",
          }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle export account statement to Excel
  const handleExportToExcel = () => {
    if (!filters.account_id || !allMovements || allMovements.length === 0) {
      toast.error(
        t("accountingReports.accountStatement.export.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    const selectedAccount = accountInfo;
    const accountName = selectedAccount
      ? isRTL
        ? selectedAccount.name_ar ||
          selectedAccount.name_en ||
          selectedAccount.name ||
          ""
        : selectedAccount.name_en ||
          selectedAccount.name_ar ||
          selectedAccount.name ||
          ""
      : "";

    const data = [
      {
        [t("accountStatement.tableHeaders.account", {
          defaultValue: "الحساب",
        })]: accountName || filters.account_id,
        [t("accountStatement.tableHeaders.period", { defaultValue: "الفترة" })]:
          `${formatDate(filters.start_date)} - ${formatDate(filters.end_date)}`,
        [t("accountStatement.tableHeaders.openingBalance", {
          defaultValue: "الرصيد الافتتاحي",
        })]: formatNumber(totals.opening_balance),
        [t("accountStatement.tableHeaders.totalDebit", {
          defaultValue: "إجمالي مدين",
        })]: formatNumber(totals.total_debit),
        [t("accountStatement.tableHeaders.totalCredit", {
          defaultValue: "إجمالي دائن",
        })]: formatNumber(totals.total_credit),
        [t("accountStatement.tableHeaders.closingBalance", {
          defaultValue: "الرصيد الختامي",
        })]: formatNumber(totals.closing_balance),
      },
      {},
      {
        [t("accountStatement.tableHeaders.debit", { defaultValue: "مدين" })]: t(
          "accountStatement.tableHeaders.debit",
          { defaultValue: "مدين" },
        ),
        [t("accountStatement.tableHeaders.credit", { defaultValue: "دائن" })]:
          t("accountStatement.tableHeaders.credit", { defaultValue: "دائن" }),
        [t("accountStatement.tableHeaders.balance", {
          defaultValue: "الرصيد",
        })]: t("accountStatement.tableHeaders.balance", {
          defaultValue: "الرصيد",
        }),
      },
      ...movements.map(movement => ({
        [t("accountStatement.tableHeaders.debit", { defaultValue: "مدين" })]:
          formatNumber(movement.debit),
        [t("accountStatement.tableHeaders.credit", { defaultValue: "دائن" })]:
          formatNumber(movement.credit),
        [t("accountStatement.tableHeaders.balance", {
          defaultValue: "الرصيد",
        })]: formatNumber(movement.balance ?? movement.running_balance),
      })),
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      t("accountingReports.accountStatementTitle", {
        defaultValue: isRTL ? "كشف الحساب" : "Account Statement",
      }),
    );
    const fileName = `AccountStatement_${accountName || filters.account_id}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("accountingReports.accountStatement.export.success", {
        defaultValue: "تم تصدير البيانات بنجاح",
      }),
      { rtl: isRTL },
    );
  };

  const renderAccountDisplay = accountId => {
    if (!accountId)
      return t("accountingReports.selectAccount", {
        defaultValue: "اختر الحساب",
      });
    const selected =
      accountInfo ||
      postingAccountList.find(
        account => String(account.id) === String(accountId),
      );
    if (!selected)
      return t("accountingReports.selectAccount", {
        defaultValue: "اختر الحساب",
      });
    return `${selected.code ? `${selected.code} - ` : ""}${isRTL ? selected.name_ar || selected.name_en || selected.name || "" : selected.name_en || selected.name_ar || selected.name || ""}`;
  };

  useEffect(() => {
    if (accountDropdownOpen && accountSearchInputRef.current) {
      accountSearchInputRef.current.focus();
    }
  }, [accountDropdownOpen]);

  // Print Template Component
  const AccountStatementPrintTemplate = ({
    movements: templateMovements,
    totals: templateTotals,
    filters: templateFilters,
    accountInfo: templateAccountInfo,
    isRTL: templateIsRTL,
    t: templateT,
    formatDate: templateFormatDate,
    formatNumber: templateFormatNumber,
    publicUrl: templatePublicUrl,
    headerLogo: templateHeaderLogo,
    chunkIndex = 0,
    isFirstPage = false,
    isLastPage = false,
  }) => {
    if (!templateMovements || templateMovements.length === 0) return null;

    const cellTextAlign = templateIsRTL ? "right" : "left";

    return (
      <div
        id={`account-statement-pdf-${chunkIndex}`}
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "794px",
          minHeight: "1123px",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "40px",
          boxSizing: "border-box",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "5px solid #000",
            padding: "3px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            minHeight: "1043px",
            height: "auto",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "2px solid #000",
              minHeight: "1033px",
              height: "1033px",
              padding: "18px 18px 10px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              direction: templateIsRTL ? "rtl" : "ltr",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              overflow: "visible",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "600px",
                backgroundImage: `url(${templateHeaderLogo})`,
                backgroundSize: "95%",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {isFirstPage && (
                <img
                  src={`${templatePublicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "visible",
                  minHeight: 0,
                  paddingBottom: "120px",
                }}
              >
                {isFirstPage && (
                  <>
                    <div
                      style={{
                        marginBottom: "12px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        textAlign: "center",
                        direction: templateIsRTL ? "rtl" : "ltr",
                      }}
                    >
                      {(() => {
                        if (!templateAccountInfo)
                          return `${templateT("accountingReports.account", { defaultValue: "الحساب" })} #${templateFilters.account_id}`;
                        const accountName = templateIsRTL
                          ? templateAccountInfo.name_ar ||
                            templateAccountInfo.name_en ||
                            templateAccountInfo.name ||
                            ""
                          : templateAccountInfo.name_en ||
                            templateAccountInfo.name_ar ||
                            templateAccountInfo.name ||
                            "";
                        return `${templateAccountInfo.code || ""} - ${accountName}`;
                      })()}
                    </div>
                    <div
                      style={{
                        marginBottom: "12px",
                        fontSize: "14px",
                        textAlign: "center",
                        direction: templateIsRTL ? "rtl" : "ltr",
                      }}
                    >
                      {templateFilters.start_date || templateFilters.end_date
                        ? `${templateT("accountingReports.period", { defaultValue: "الفترة" })}: ${templateFormatDate(templateFilters.start_date) || "—"} - ${templateFormatDate(templateFilters.end_date) || "—"}`
                        : templateT("accountingReports.accountStatementTitle", {
                            defaultValue: "كشف الحساب",
                          })}
                    </div>
                  </>
                )}
                <div
                  style={{
                    marginTop: "12px",
                    direction: templateIsRTL ? "rtl" : "ltr",
                    fontFamily: '"Times New Roman", "Times", serif',
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "11px",
                      border: "1px solid #000",
                      fontFamily: '"Times New Roman", "Times", serif',
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      direction: templateIsRTL ? "rtl" : "ltr",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}
                      >
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {templateT("accountingReports.debit", {
                            defaultValue: "مدين",
                          })}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {templateT("accountingReports.credit", {
                            defaultValue: "دائن",
                          })}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {templateT("accountingReports.balance", {
                            defaultValue: "الرصيد",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {templateMovements.map((movement, idx) => (
                        <tr
                          key={movement.id || idx}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {templateFormatNumber(movement.debit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {templateFormatNumber(movement.credit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {templateFormatNumber(
                              movement.balance ?? movement.running_balance,
                            )}
                          </td>
                        </tr>
                      ))}
                      {isLastPage && templateTotals && (
                        <tr
                          style={{
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {templateFormatNumber(templateTotals.total_debit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {templateFormatNumber(templateTotals.total_credit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {templateFormatNumber(
                              templateTotals.closing_balance,
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {isLastPage && (
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "18px",
                  right: "18px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={`${templatePublicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Preview Template Component
  const AccountStatementPreviewTemplate = ({
    movements: previewMovements,
    totals: previewTotals,
    filters: previewFilters,
    accountInfo: previewAccountInfo,
    postingAccounts: previewPostingAccounts,
    isRTL: previewIsRTL,
    t: previewT,
    formatDate: previewFormatDate,
    formatNumber: previewFormatNumber,
    publicUrl: previewPublicUrl,
    headerLogo: previewHeaderLogo,
    chunkIndex = 0,
    isFirstPage = false,
    isLastPage = false,
    totalChunks = 1,
  }) => {
    if (!previewMovements || previewMovements.length === 0) return null;

    const cellTextAlign = previewIsRTL ? "right" : "left";

    const selectedAccount =
      previewAccountInfo ||
      previewPostingAccounts.find(
        a => String(a.id) === String(previewFilters.account_id),
      );

    return (
      <div
        style={{
          width: "794px",
          minHeight: "1123px",
          height: "auto",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "40px",
          boxSizing: "border-box",
          margin: "0 auto",
          overflow: "visible",
          marginBottom: chunkIndex < totalChunks - 1 ? "20px" : 0,
        }}
      >
        <div
          style={{
            border: "5px solid #000",
            padding: "3px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            minHeight: "1043px",
            height: "auto",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "2px solid #000",
              minHeight: "1033px",
              height: "1033px",
              padding: "18px 18px 10px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              direction: previewIsRTL ? "rtl" : "ltr",
              backgroundColor: "#ffffff",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${previewHeaderLogo})`,
                backgroundSize: "95%",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {isFirstPage && (
                <img
                  src={`${previewPublicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "visible",
                  minHeight: 0,
                  paddingBottom: "120px",
                }}
              >
                {isFirstPage && (
                  <>
                    <div
                      style={{
                        marginBottom: "12px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        textAlign: "center",
                        direction: previewIsRTL ? "rtl" : "ltr",
                      }}
                    >
                      {selectedAccount
                        ? `${selectedAccount.code || ""} - ${previewIsRTL ? selectedAccount.name_ar || selectedAccount.name || "" : selectedAccount.name_en || selectedAccount.name_ar || selectedAccount.name || ""}`
                        : `${previewT("accountingReports.account", { defaultValue: "الحساب" })} #${previewFilters.account_id}`}
                    </div>
                    <div
                      style={{
                        marginBottom: "12px",
                        fontSize: "14px",
                        textAlign: "center",
                        direction: previewIsRTL ? "rtl" : "ltr",
                      }}
                    >
                      {previewFilters.start_date || previewFilters.end_date
                        ? `${previewT("accountingReports.period", { defaultValue: "الفترة" })}: ${previewFormatDate(previewFilters.start_date) || "—"} - ${previewFormatDate(previewFilters.end_date) || "—"}`
                        : previewT("accountingReports.accountStatementTitle", {
                            defaultValue: "كشف الحساب",
                          })}
                    </div>
                  </>
                )}
                <div
                  style={{
                    marginTop: "12px",
                    direction: previewIsRTL ? "rtl" : "ltr",
                    fontFamily: '"Times New Roman", "Times", serif',
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "11px",
                      border: "1px solid #000",
                      fontFamily: '"Times New Roman", "Times", serif',
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      direction: previewIsRTL ? "rtl" : "ltr",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "rgba(238, 242, 247, 0.7)" }}
                      >
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {previewT("accountingReports.debit", {
                            defaultValue: "مدين",
                          })}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {previewT("accountingReports.credit", {
                            defaultValue: "دائن",
                          })}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: cellTextAlign,
                            fontWeight: "700",
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                          }}
                        >
                          {previewT("accountingReports.balance", {
                            defaultValue: "الرصيد",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewMovements.map((movement, idx) => (
                        <tr
                          key={movement.id || idx}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                          }}
                        >
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {previewFormatNumber(movement.debit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {previewFormatNumber(movement.credit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                            }}
                          >
                            {previewFormatNumber(
                              movement.balance ?? movement.running_balance,
                            )}
                          </td>
                        </tr>
                      ))}
                      {isLastPage && previewTotals && (
                        <tr
                          style={{
                            backgroundColor: "rgba(238, 242, 247, 0.7)",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {previewFormatNumber(previewTotals.total_debit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {previewFormatNumber(previewTotals.total_credit)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: cellTextAlign,
                              backgroundColor: "rgba(238, 242, 247, 0.7)",
                            }}
                          >
                            {previewFormatNumber(previewTotals.closing_balance)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {isLastPage && (
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "18px",
                  right: "18px",
                  flexShrink: 0,
                }}
              >
                <img
                  src={`${previewPublicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "24px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              margin: 0,
              direction: dir,
              textAlign,
            }}
          >
            {t("accountingReports.accountStatementTitle", {
              defaultValue: "كشف الحساب",
            })}
          </h1>
        </div>
        {filters.account_id && allMovements && allMovements.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              direction: dir,
            }}
          >
            <button
              onClick={handlePreviewStatement}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                direction: dir,
                textAlign: textAlign,
              }}
            >
              <FaEye />{" "}
              {t("accountingReports.accountStatement.preview.button", {
                defaultValue: "معاينة",
              })}
            </button>
            <button
              onClick={handlePrintStatement}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: isGeneratingPdf ? 0.6 : 1,
                direction: dir,
                textAlign: textAlign,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("accountingReports.accountStatement.print.generating", {
                    defaultValue: "جاري الطباعة...",
                  })
                : t("accountingReports.accountStatement.print.title", {
                    defaultValue: "طباعة",
                  })}
            </button>
            <button
              onClick={handleExportToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                direction: dir,
                textAlign: textAlign,
              }}
            >
              <FaFileExcel />{" "}
              {t("accountingReports.accountStatement.export.title", {
                defaultValue: "تصدير Excel",
              })}
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
          display: "grid",
          gap: "16px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
          }}
        >
          <div style={{ position: "relative" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            >
              {t("accountingReports.account", { defaultValue: "الحساب" })}{" "}
              <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <div
              onClick={() => {
                setAccountDropdownOpen(prev => !prev);
                if (!accountDropdownOpen) {
                  setTimeout(() => {
                    if (accountSearchInputRef.current) {
                      accountSearchInputRef.current.focus();
                    }
                  }, 100);
                }
              }}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              <span>{renderAccountDisplay(filters.account_id)}</span>
              <span style={{ fontSize: "12px" }}>▼</span>
            </div>
            {accountDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "0 0 8px 8px",
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                  marginTop: "4px",
                }}
                onClick={e => e.stopPropagation()}
              >
                <input
                  ref={accountSearchInputRef}
                  type="text"
                  placeholder={t("accountingReports.searchAccount", {
                    defaultValue: "ابحث عن الحساب...",
                  })}
                  value={accountSearchTerm}
                  onChange={e => {
                    const value = e.target.value;
                    setAccountSearchTerm(value);
                    if (value) {
                      dispatch(getPostingAccounts(value));
                    } else {
                      dispatch(getPostingAccounts());
                    }
                  }}
                  onClick={e => e.stopPropagation()}
                  style={{
                    width: "100%",
                    padding: "10px",
                    backgroundColor: "#2d3748",
                    border: "none",
                    borderBottom: "1px solid #333",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                  {filteredAccounts.length > 0 ? (
                    filteredAccounts.map(account => {
                      const isSelected =
                        String(filters.account_id) === String(account.id);
                      return (
                        <div
                          key={account.id || account.code}
                          onClick={() => {
                            if (isSelected) return;
                            setFilters(prev => ({
                              ...prev,
                              account_id: String(account.id),
                            }));
                            setAccountDropdownOpen(false);
                            setAccountSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: isSelected ? "not-allowed" : "pointer",
                            backgroundColor: isSelected
                              ? "#2a2a2a"
                              : "transparent",
                            color: isSelected ? "#888" : "white",
                            borderBottom: "1px solid #333",
                            opacity: isSelected ? 0.6 : 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onMouseEnter={e => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = "#374151";
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <span>
                            {account.code} -{" "}
                            {isRTL
                              ? account.name_ar || account.name || ""
                              : account.name || account.name_ar || ""}
                          </span>
                          {isSelected && (
                            <span
                              style={{ fontSize: "12px", color: "#f59f00" }}
                            >
                              {t("accountingReports.accountAlreadySelected", {
                                defaultValue: "هذا الحساب مختار بالفعل",
                              })}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        padding: "10px",
                        color: "#888",
                        textAlign: "center",
                      }}
                    >
                      {t("accountingReports.noAccounts", {
                        defaultValue: "لا توجد نتائج",
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
            {accountDropdownOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 999,
                }}
                onClick={() => {
                  setAccountDropdownOpen(false);
                }}
              />
            )}
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            >
              {t("accountingReports.startDate", {
                defaultValue: "تاريخ البداية",
              })}{" "}
              ({t("accountingReports.optional", { defaultValue: "اختياري" })})
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            >
              {t("accountingReports.endDate", {
                defaultValue: "تاريخ النهاية",
              })}{" "}
              ({t("accountingReports.optional", { defaultValue: "اختياري" })})
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={() => {
              setFilters({ account_id: "", start_date: "", end_date: "" });
              setAccountSearchTerm("");
              setAccountDropdownOpen(false);
              dispatch(getPostingAccounts());
              dispatch(clearAccountStatement());
            }}
            style={{
              backgroundColor: "#666",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            {t("accountingReports.reset", { defaultValue: "إعادة تعيين" })}
          </button>
          <button
            type="submit"
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            {t("accountingReports.showStatement", {
              defaultValue: "عرض الكشف",
            })}
          </button>
        </div>
      </form>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        {[
          {
            label: t("accountingReports.openingBalance", {
              defaultValue: "الرصيد الافتتاحي",
            }),
            value: totals.opening_balance,
          },
          {
            label: t("accountingReports.totalDebit", {
              defaultValue: "إجمالي مدين",
            }),
            value: totals.total_debit,
          },
          {
            label: t("accountingReports.totalCredit", {
              defaultValue: "إجمالي دائن",
            }),
            value: totals.total_credit,
          },
          {
            label: t("accountingReports.closingBalance", {
              defaultValue: "الرصيد الختامي",
            }),
            value: totals.closing_balance,
          },
        ].map(item => (
          <div
            key={item.label}
            style={{
              backgroundColor: "#1b3350",
              border: "1px solid #333",
              borderRadius: "12px",
              padding: "18px",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#9ca3af",
                marginBottom: "6px",
                direction: dir,
                textAlign: textAlign,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "white",
                direction: dir,
                textAlign: textAlign,
              }}
            >
              {formatNumber(item.value)}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: "720px",
              direction: dir,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                {[
                  t("accountingReports.debit", { defaultValue: "مدين" }),
                  t("accountingReports.credit", { defaultValue: "دائن" }),
                  t("accountingReports.balance", { defaultValue: "الرصيد" }),
                ].map(heading => (
                  <th
                    key={heading}
                    style={{
                      padding: "18px 16px",
                      textAlign: textAlign,
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderBottom: "1px solid #333",
                      direction: dir,
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filters.account_id ? (
                movements && movements.length > 0 ? (
                  movements.map((movement, idx) => (
                    <tr
                      key={movement.id || idx}
                      style={{
                        borderBottom: "1px solid #333",
                        backgroundColor: idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: textAlign,
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {formatNumber(movement.debit)}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: textAlign,
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {formatNumber(movement.credit)}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: textAlign,
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {formatNumber(
                          movement.balance ?? movement.running_balance,
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "30px",
                        textAlign: textAlign,
                        color: "#9ca3af",
                        fontSize: "16px",
                        direction: dir,
                      }}
                    >
                      {t("accountingReports.noMovements", {
                        defaultValue:
                          "لا توجد حركات لهذا الحساب في الفترة المحددة.",
                      })}
                    </td>
                  </tr>
                )
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: "30px",
                      textAlign: textAlign,
                      color: "#9ca3af",
                      fontSize: "16px",
                      direction: dir,
                    }}
                  >
                    {t("accountingReports.pleaseSelectAccount", {
                      defaultValue: "يرجى اختيار حساب لعرض كشف الحركة.",
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filters.account_id && totalPages > 1 && (
          <div
            style={{
              padding: "18px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              borderTop: "1px solid #333",
            }}
          >
            <button
              onClick={() => handleLocalPageChange(Math.max(1, localPage - 1))}
              disabled={localPage === 1}
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor: localPage > 1 ? "pointer" : "not-allowed",
                opacity: localPage > 1 ? 1 : 0.5,
                fontSize: "14px",
              }}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={`page-${page}`}
                onClick={() => handleLocalPageChange(page)}
                style={{
                  backgroundColor: page === localPage ? "#AC2000" : "#1b3350",
                  color: "white",
                  border: page === localPage ? "none" : "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: page === localPage ? "bold" : "normal",
                }}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() =>
                handleLocalPageChange(Math.min(totalPages, localPage + 1))
              }
              disabled={localPage === totalPages}
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor: localPage < totalPages ? "pointer" : "not-allowed",
                opacity: localPage < totalPages ? 1 : 0.5,
                fontSize: "14px",
              }}
            >
              &gt;
            </button>
          </div>
        )}
      </div>

      {/* Print Templates - Multiple pages */}
      {filters.account_id &&
        allMovementsForPDF &&
        allMovementsForPDF.length > 0 &&
        printPdfChunks.length > 0 &&
        printPdfChunks.map((chunk, chunkIndex) => {
          const isLastPage = chunkIndex === printPdfChunks.length - 1;
          return (
            <AccountStatementPrintTemplate
              key={chunkIndex}
              chunkIndex={chunkIndex}
              movements={chunk.items}
              accountInfo={accountInfo}
              totals={isLastPage ? totals : null}
              filters={filters}
              isRTL={isRTL}
              t={t}
              formatDate={formatDate}
              formatNumber={formatNumber}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              isFirstPage={chunkIndex === 0}
              isLastPage={isLastPage}
            />
          );
        })}

      {/* PDF Preview Modal */}
      {isPreviewOpen &&
        filters.account_id &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              padding: "20px",
              overflow: "hidden",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
                setPdfChunks([]);
              }
            }}
            onWheel={e => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setPreviewZoom(prev =>
                  Math.max(0.3, Math.min(2, prev + delta)),
                );
              }
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "12px",
                padding: "20px",
                width: "100%",
                height: "100%",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexShrink: 0,
                  direction: dir,
                }}
              >
                <h2
                  style={{
                    color: "white",
                    margin: 0,
                    fontSize: "20px",
                    direction: dir,
                    textAlign: textAlign,
                  }}
                >
                  {t("accountingReports.accountStatement.preview.title", {
                    defaultValue: "معاينة كشف الحساب",
                  })}
                </h2>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      marginRight: isRTL ? "10px" : "0",
                      marginLeft: isRTL ? "0" : "10px",
                    }}
                  >
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.max(0.3, prev - 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                        direction: dir,
                        textAlign: textAlign,
                      }}
                      title={t(
                        "accountingReports.accountStatement.preview.zoomOut",
                        { defaultValue: "تصغير" },
                      )}
                    >
                      −
                    </button>
                    <span
                      style={{
                        color: "white",
                        minWidth: "60px",
                        textAlign: "center",
                        fontSize: "14px",
                      }}
                    >
                      {Math.round(previewZoom * 100)}%
                    </span>
                    <button
                      onClick={() =>
                        setPreviewZoom(prev => Math.min(2, prev + 0.1))
                      }
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: "bold",
                        direction: dir,
                        textAlign: textAlign,
                      }}
                      title={t(
                        "accountingReports.accountStatement.preview.zoomIn",
                        { defaultValue: "تكبير" },
                      )}
                    >
                      +
                    </button>
                    <button
                      onClick={handleResetPreview}
                      style={{
                        backgroundColor: "#444",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontSize: "12px",
                        direction: dir,
                        textAlign: textAlign,
                      }}
                      title={t(
                        "accountingReports.accountStatement.preview.fitToScreen",
                        { defaultValue: "ملاءمة الشاشة" },
                      )}
                    >
                      {t("accountingReports.accountStatement.preview.fit", {
                        defaultValue: "ملاءمة",
                      })}
                    </button>
                  </div>
                  <button
                    onClick={handlePrintFromPreview}
                    disabled={isGeneratingPdf}
                    style={{
                      backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: isGeneratingPdf ? 0.6 : 1,
                      direction: dir,
                      textAlign: textAlign,
                    }}
                  >
                    <FaPrint />{" "}
                    {t("accountingReports.accountStatement.print.title", {
                      defaultValue: "طباعة",
                    })}
                  </button>
                  <button
                    onClick={() => {
                      setIsPreviewOpen(false);
                    }}
                    style={{
                      backgroundColor: "#666",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 20px",
                      cursor: "pointer",
                      direction: dir,
                      textAlign: textAlign,
                    }}
                  >
                    {t("accountingReports.accountStatement.preview.close", {
                      defaultValue: "إغلاق",
                    })}
                  </button>
                </div>
              </div>
              <div
                ref={previewContainerRef}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  padding: "20px",
                  overflow: previewZoom <= 1.0 ? "auto" : "hidden",
                  flex: 1,
                  display: "flex",
                  alignItems: previewZoom <= 1.0 ? "flex-start" : "center",
                  justifyContent: "center",
                  minHeight: 0,
                  position: "relative",
                  cursor:
                    previewZoom > 1.0 && isDragging
                      ? "grabbing"
                      : previewZoom > 1.0
                        ? "grab"
                        : "default",
                }}
                onMouseDown={previewZoom > 1.0 ? handleMouseDown : undefined}
              >
                <div
                  style={{
                    transform:
                      previewZoom > 1.0
                        ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                        : `scale(${previewZoom})`,
                    transformOrigin:
                      previewZoom <= 1.0 ? "top center" : "center center",
                    transition: isDragging ? "none" : "transform 0.2s ease",
                    width: "794px",
                    height: "1123px",
                    userSelect: previewZoom > 1.0 ? "none" : "auto",
                  }}
                >
                  {(pdfChunks || []).map((chunk, chunkIndex) => {
                    const isLastPage = chunkIndex === pdfChunks.length - 1;
                    return (
                      <AccountStatementPreviewTemplate
                        key={chunkIndex}
                        chunkIndex={chunkIndex}
                        movements={chunk.items}
                        accountInfo={accountInfo}
                        totals={isLastPage ? totals : null}
                        filters={filters}
                        postingAccounts={postingAccounts}
                        isRTL={isRTL}
                        t={t}
                        formatDate={formatDate}
                        formatNumber={formatNumber}
                        publicUrl={publicUrl}
                        headerLogo={headerLogo}
                        isFirstPage={chunkIndex === 0}
                        isLastPage={isLastPage}
                        totalChunks={pdfChunks.length}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="account-statement-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.accountStatement.title')}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{filters.start_date} - {filters.end_date}</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="account-statement-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.accountStatement.date')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.accountStatement.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.accountStatement.debit')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.accountStatement.credit')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.accountStatement.balance')}</th>
            </tr>
          </thead>
          <tbody id="account-statement-pdf-measure-tbody">
            {allMovements.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{item.date}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{item.description}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{item.debit}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{item.credit}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{item.balance}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="account-statement-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

export default AccountStatement;
